import { Application, Container, Graphics, Sprite, Text, Texture } from 'pixi.js';
import type { CarState } from '../sim/car';
import type { TeamSpec } from '../sim/teams';
import type { Track } from '../sim/track';
import { buildCar } from './carArt';
import { buildTrackArt } from './trackArt';

/** World pixels visible vertically; width follows the screen's aspect. */
const VIEW_H = 300;
const LOOK_AHEAD_S = 0.32;
const LOOK_AHEAD_MAX = 120;

export interface CarView {
  x: number;
  y: number;
  h: number;
  vx: number;
  vy: number;
  crashed: boolean;
}

export function lerpCar(a: CarState, b: CarState, t: number): CarView {
  let dh = b.h - a.h;
  if (dh > Math.PI) dh -= 2 * Math.PI;
  if (dh < -Math.PI) dh += 2 * Math.PI;
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    h: a.h + dh * t,
    vx: b.vx,
    vy: b.vy,
    crashed: b.crashed,
  };
}

export class Renderer {
  readonly app = new Application();
  private world = new Container();
  private trackLayer = new Container();
  private skids = new Graphics();
  private skidCount = 0;
  private car: Container | null = null;
  private ghost: Container | null = null;
  private crashFx = new Graphics();
  private rivals: { car: Container; tag: Text }[] = [];
  private rivalLayer = new Container();
  /** Head-to-Head: darkens everything outside the shared view box. */
  private shade = new Graphics();
  /** Daily conditions (design.md B14.3): rain and night overlays. */
  private weather = new Graphics();
  private night: Sprite | null = null;
  private conditions: 'dry' | 'wet' | 'night' = 'dry';
  private rain: { x: number; y: number; v: number }[] = [];
  private camX = 0;
  private camY = 0;
  private camInit = false;
  private lookX = 0;
  private lookY = 0;
  classicCamera = false;
  /** Where the car sits vertically (fraction of screen height). */
  anchorY = 0.46;

  async init(host: HTMLElement): Promise<void> {
    await this.app.init({
      resizeTo: host,
      antialias: true,
      background: 0x1b1712,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
      preference: 'webgl',
    });
    host.appendChild(this.app.canvas);
    this.world.addChild(this.trackLayer, this.skids, this.rivalLayer, this.crashFx);
    this.app.stage.addChild(this.world, this.shade, this.weather);
  }

  setConditions(c: 'dry' | 'wet' | 'night'): void {
    this.conditions = c;
    this.weather.clear();
    if (c === 'night' && !this.night) {
      // Radial falloff: clear around the car, near-black at the edges.
      const cv = document.createElement('canvas');
      cv.width = cv.height = 512;
      const g = cv.getContext('2d')!;
      const grad = g.createRadialGradient(256, 256, 20, 256, 256, 256);
      grad.addColorStop(0, 'rgba(5,6,20,0)');
      grad.addColorStop(0.28, 'rgba(5,6,20,0.25)');
      grad.addColorStop(0.62, 'rgba(5,6,20,0.88)');
      grad.addColorStop(1, 'rgba(5,6,20,0.94)');
      g.fillStyle = grad;
      g.fillRect(0, 0, 512, 512);
      this.night = new Sprite(Texture.from(cv));
      this.night.anchor.set(0.5);
      this.app.stage.addChild(this.night);
    }
    if (this.night) this.night.visible = c === 'night';
    if (c === 'wet' && !this.rain.length) {
      for (let i = 0; i < 90; i++) this.rain.push({ x: Math.random(), y: Math.random(), v: 0.9 + Math.random() * 0.6 });
    }
  }

  /** Screen-space weather for the frame (car at sx, sy on screen). */
  private drawWeather(sx: number, sy: number, dtFrame: number): void {
    const sw = this.app.screen.width;
    const sh = this.app.screen.height;
    const w = this.weather;
    w.clear();
    if (this.conditions === 'wet') {
      w.rect(0, 0, sw, sh).fill({ color: 0x1a2a48, alpha: 0.2 });
      for (const d of this.rain) {
        d.y += dtFrame * d.v * 1.6;
        d.x -= dtFrame * 0.25;
        if (d.y > 1) d.y -= 1;
        if (d.x < 0) d.x += 1;
        const x = d.x * sw;
        const y = d.y * sh;
        w.moveTo(x, y).lineTo(x - 4, y + 14);
      }
      w.stroke({ color: 0xbfd4ff, alpha: 0.45, width: 1 });
    }
    if (this.night && this.conditions === 'night') {
      const r = Math.hypot(sw, sh) * 1.25;
      this.night.position.set(sx, sy);
      this.night.width = this.night.height = r * 2;
    }
  }

  setTrack(track: Track): void {
    this.trackLayer.removeChildren().forEach((c) => c.destroy({ children: true }));
    this.trackLayer.addChild(buildTrackArt(track));
    this.clearSkids();
    this.camInit = false;
  }

  setTeam(team: TeamSpec): void {
    if (this.car) this.car.destroy({ children: true });
    this.car = buildCar(team);
    this.world.addChild(this.car);
  }

  setGhost(team: TeamSpec | null): void {
    if (this.ghost) {
      this.ghost.destroy({ children: true });
      this.ghost = null;
    }
    if (team) {
      this.ghost = buildCar(team, 0.38);
      this.world.addChildAt(this.ghost, this.world.getChildIndex(this.crashFx));
    }
  }

  /** Other cars in a race, each with a 3-letter name tag. */
  setRivals(list: { team: TeamSpec; tag: string; color?: number }[]): void {
    this.rivalLayer.removeChildren().forEach((c) => c.destroy({ children: true }));
    this.rivals = list.map(({ team, tag, color }) => {
      const car = buildCar(team);
      const label = new Text({
        text: tag,
        style: { fontFamily: '"Press Start 2P", monospace', fontSize: color === undefined ? 6 : 7, fill: color ?? 0xffffff, stroke: { color: 0x000000, width: 2 } },
        resolution: 4,
      });
      label.anchor.set(0.5, 1);
      this.rivalLayer.addChild(car, label);
      return { car, tag: label };
    });
  }

  clearSkids(): void {
    this.skids.clear();
    this.skidCount = 0;
  }

  /** Render-only tyre marks when the car slides or runs off the tarmac. */
  addSkid(v: CarView, intensity: number): void {
    if (this.skidCount > 6000) this.clearSkids();
    const c = Math.cos(v.h);
    const s = Math.sin(v.h);
    for (const side of [-4, 4]) {
      const x = v.x - c * 7 - s * side;
      const y = v.y - s * 7 + c * side;
      this.skids.circle(x, y, 1.3).fill({ color: 0x151515, alpha: 0.12 + 0.25 * intensity });
    }
    this.skidCount += 2;
  }

  draw(
    car: CarView,
    ghost: CarView | null,
    dtFrame: number,
    flashes: { x: number; y: number; f: number }[] = [],
    rivals: (CarView & { alpha: number })[] = [],
  ): void {
    const screenW = this.app.screen.width;
    const screenH = this.app.screen.height;
    const scale = screenH / VIEW_H;

    // Look-ahead camera (design.md B9); smoothed, render-only.
    const tx: [number, number] = this.classicCamera ? [0, 0] : clampLen(car.vx * LOOK_AHEAD_S, car.vy * LOOK_AHEAD_S, LOOK_AHEAD_MAX);
    const k = 1 - Math.exp(-dtFrame * 4);
    this.lookX += (tx[0] - this.lookX) * k;
    this.lookY += (tx[1] - this.lookY) * k;
    const wantX = car.x + this.lookX;
    const wantY = car.y + this.lookY;
    if (!this.camInit) {
      this.camX = wantX;
      this.camY = wantY;
      this.camInit = true;
    } else {
      const kc = 1 - Math.exp(-dtFrame * 14);
      this.camX += (wantX - this.camX) * kc;
      this.camY += (wantY - this.camY) * kc;
    }
    this.shade.clear();
    if (this.car) this.car.visible = true;
    this.world.scale.set(scale);
    // Keep a little room for the HUD strip at the bottom.
    this.world.position.set(screenW / 2 - this.camX * scale, screenH * this.anchorY - this.camY * scale);

    if (this.car) {
      this.car.position.set(car.x, car.y);
      this.car.rotation = car.h;
      this.car.alpha = car.crashed ? 0.55 : 1;
    }
    this.drawWeather(this.world.position.x + car.x * scale, this.world.position.y + car.y * scale, dtFrame);
    if (this.ghost) {
      this.ghost.visible = !!ghost;
      if (ghost) {
        this.ghost.position.set(ghost.x, ghost.y);
        this.ghost.rotation = ghost.h;
      }
    }
    this.rivals.forEach((rv, i) => {
      const v = rivals[i];
      const show = !!v && v.alpha > 0.01;
      rv.car.visible = show;
      rv.tag.visible = show;
      if (!v || !show) return;
      rv.car.position.set(v.x, v.y);
      rv.car.rotation = v.h;
      rv.car.alpha = v.alpha * (v.crashed ? 0.55 : 1);
      rv.tag.position.set(v.x, v.y - 10);
      rv.tag.alpha = v.alpha;
    });
    this.drawFlashes(flashes);
  }

  /**
   * Head-to-Head (design.md B8.1): one shared camera, every car drawn as a
   * tagged rival. The sim's view box (boxW × boxH world px, × boxScale in
   * sudden death) is fitted to the screen; anything outside it is shaded,
   * because a car out there is about to be dropped.
   */
  drawShared(
    camX: number,
    camY: number,
    boxW: number,
    boxH: number,
    boxScale: number,
    cars: (CarView & { alpha: number })[],
    flashes: { x: number; y: number; f: number }[],
    danger: boolean,
    shadeOutside = true,
  ): void {
    const sw = this.app.screen.width;
    const sh = this.app.screen.height;
    const scale = Math.min(sw / boxW, sh / boxH);
    this.world.scale.set(scale);
    this.world.position.set(sw / 2 - camX * scale, sh / 2 - camY * scale);
    if (this.car) this.car.visible = false;
    if (this.ghost) this.ghost.visible = false;
    this.camInit = false;
    this.rivals.forEach((rv, i) => {
      const v = cars[i];
      const show = !!v && v.alpha > 0.01;
      rv.car.visible = show;
      rv.tag.visible = show;
      if (!v || !show) return;
      rv.car.position.set(v.x, v.y);
      rv.car.rotation = v.h;
      rv.car.alpha = v.alpha * (v.crashed ? 0.55 : 1);
      rv.tag.position.set(v.x, v.y - 10);
      rv.tag.alpha = v.alpha;
    });
    this.drawFlashes(flashes);

    // Shade outside the (possibly shrinking) box.
    const w = boxW * boxScale * scale;
    const h = boxH * boxScale * scale;
    const x0 = (sw - w) / 2;
    const y0 = (sh - h) / 2;
    const g = this.shade;
    g.clear();
    if (!shadeOutside) return;
    const a = 0.5;
    g.rect(0, 0, sw, y0).fill({ color: 0x0d0a1f, alpha: a });
    g.rect(0, y0 + h, sw, sh - y0 - h).fill({ color: 0x0d0a1f, alpha: a });
    g.rect(0, y0, x0, h).fill({ color: 0x0d0a1f, alpha: a });
    g.rect(x0 + w, y0, sw - x0 - w, h).fill({ color: 0x0d0a1f, alpha: a });
    g.rect(x0, y0, w, h).stroke({ color: danger ? 0xff4040 : 0xffffff, alpha: danger ? 0.8 : 0.25, width: danger ? 3 : 2 });
  }

  private drawFlashes(flashes: { x: number; y: number; f: number }[]): void {
    this.crashFx.clear();
    for (const fl of flashes) {
      if (fl.f <= 0) continue;
      const r = 10 + (1 - fl.f) * 26;
      this.crashFx.circle(fl.x, fl.y, r).fill({ color: 0xffa020, alpha: 0.55 * fl.f });
      this.crashFx.circle(fl.x, fl.y, r * 0.55).fill({ color: 0xfff0a0, alpha: 0.8 * fl.f });
    }
  }
}

function clampLen(x: number, y: number, max: number): [number, number] {
  const l = Math.hypot(x, y);
  if (l <= max) return [x, y];
  return [(x / l) * max, (y / l) * max];
}
